import { useState, useEffect } from 'react'

/* global Office */

type ColorScheme = 'light' | 'dark'

/**
 * Determines if a hex color is dark by calculating its perceived brightness.
 * Uses the luminance formula: (0.299*R + 0.587*G + 0.114*B)
 */
function isDarkColor(hex: string): boolean {
  const color = hex.replace('#', '')
  const r = Number.parseInt(color.substring(0, 2), 16)
  const g = Number.parseInt(color.substring(2, 4), 16)
  const b = Number.parseInt(color.substring(4, 6), 16)
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return brightness < 0.5
}

/**
 * Detects the Outlook theme from Office.context.officeTheme.
 */
function detectOfficeTheme(): ColorScheme {
  try {
    const officeTheme = Office?.context?.officeTheme
    if (officeTheme?.bodyBackgroundColor) {
      return isDarkColor(officeTheme.bodyBackgroundColor) ? 'dark' : 'light'
    }
  } catch {
    // Office API not available
  }
  return 'light'
}

/**
 * Hook that detects and follows Outlook's dark/light theme.
 * In DEV mode, falls back to the OS prefers-color-scheme preference.
 */
export function useOfficeTheme(): ColorScheme {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    if (import.meta.env.DEV) {
      return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return detectOfficeTheme()
  })

  useEffect(() => {
    if (import.meta.env.DEV) {
      // In dev mode, listen to OS theme changes
      const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        setColorScheme(e.matches ? 'dark' : 'light')
      }
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }

    // In production, listen to Outlook theme changes
    try {
      Office.context.mailbox.addHandlerAsync(Office.EventType.OfficeThemeChanged, () => {
        setColorScheme(detectOfficeTheme())
      })
    } catch {
      // Event not supported in this Outlook version
    }
  }, [])

  return colorScheme
}
